/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import {
  ExpandMoreIcon,
  FileDownloadIcon,
  FileUploadIcon
} from "@dotnet/telematics-design-system";

import AddDriversIcon from "../../../../assets/customIcons/AddDriversIcon";
import AddVehiclesIcon from "../../../../assets/customIcons/AddVehiclesIcon";
import {
  COMPANIES_ACTIONS,
  DRIVER_FILES_ACTIONS,
  DRIVERS_ACTIONS,
  INTEGRATIONS_ACTIONS,
  MORE_ACTIONS_KEYS,
  ROUTES,
  TUTORIAL_IDS,
  useMediaQueries,
  USERS_ACTIONS,
  VEHICLE_FILES_ACTIONS,
  VEHICLES_ACTIONS
} from "../../../../shared";
import Button, {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "../../../Button/Button";
import ListChoice from "../../../ListChoice/ListChoice";
import Menu from "../../../Menu/Menu";
import { useMenu } from "../../../Table/hooks/useMenu";
import useTutorialMode from "../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../TutorialMode/types";
import { Actions } from "../types/types";

interface IPageHeaderActionsProps {
  route: ROUTES;
  children: ReactNode;
  moreActions?: MORE_ACTIONS_KEYS[];
  addItem?: boolean;
  importItem?: boolean;
  download?: boolean;
  fileSettings?: boolean;
  inviteUser?: boolean;
  moveItem?: boolean;
  openDialog: (action: Actions) => void;
}

const PageHeaderActions = ({
  route,
  children,
  moreActions,
  addItem,
  importItem,
  download,
  fileSettings,
  inviteUser,
  moveItem,
  openDialog
}: IPageHeaderActionsProps) => {
  const { toSm, fromLg } = useMediaQueries();
  const { t } = useTranslation();

  const { checkIfCorrectTutorialStep, setTutorialDetails, stepGroup } =
    useTutorialMode();
  const { anchorEl, handleOpenMenu, handleCloseMenu } = useMenu();

  const {
    initialLoading,
    selectedRows,
    data: tableRowData,
    params: { desktopDetailsSection }
  } = useTableStoreByRoute(route);

  const { resetDetailsSection } = useDetailsStore();

  const actionButtonLoaderWidthToSm = "100%";

  const handleAddVehicle = () => {
    if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialAddVehicle, 0)) {
      setTutorialDetails({
        isTutorialOpen: false,
        stepIndex: 0,
        stepGroup,
        isTutorialActive: true
      });
    }

    openDialog(VEHICLES_ACTIONS.AddVehicle);
  };

  const handleAddItemClick = () => {
    switch (route) {
      case ROUTES.Vehicles:
        return handleAddVehicle();
      case ROUTES.Drivers:
        return openDialog(DRIVERS_ACTIONS.AddDriver);
      case ROUTES.Companies:
        return openDialog(COMPANIES_ACTIONS.CreateCompany);
      case ROUTES.Integrations:
      case ROUTES.IntegrationsApi:
      case ROUTES.AnalysisProviders:
        return openDialog(INTEGRATIONS_ACTIONS.AddIntegration);

      default:
        return;
    }
  };

  const isVehicles = route === ROUTES.Vehicles;

  const handleImportItemClick = () => {
    handleCloseMenu();

    const importAction = isVehicles
      ? VEHICLES_ACTIONS.ImportVehicle
      : DRIVERS_ACTIONS.ImportDriver;

    return openDialog(importAction);
  };

  const handleExportItemClick = () => {
    handleCloseMenu();

    const exportAction = isVehicles
      ? VEHICLES_ACTIONS.ExportVehicle
      : DRIVERS_ACTIONS.ExportDriver;

    return openDialog(exportAction);
  };

  const generateAddItemButtonText = () => {
    switch (route) {
      case ROUTES.Vehicles:
        return t("Button##add vehicle");
      case ROUTES.Drivers:
        return t("Button##add driver");
      case ROUTES.Companies:
        return t("Button##create");
      case ROUTES.Integrations:
      case ROUTES.IntegrationsApi:
      case ROUTES.AnalysisProviders:
        return t("Button##add integration");

      default:
        return "add item";
    }
  };

  const generateImportItemButtonText = () => {
    if (route === ROUTES.Vehicles) {
      return t("Button##import vehicles");
    }

    if (route === ROUTES.Drivers) {
      return t("Button##import drivers");
    }

    return "";
  };

  const generateExportItemButtonText = () => {
    if (route === ROUTES.Vehicles) {
      return t("Button##export vehicles");
    }

    if (route === ROUTES.Drivers) {
      return t("Button##export drivers");
    }

    return "";
  };

  const getAddItemIcon = () => {
    if (route === ROUTES.Vehicles) {
      return <AddVehiclesIcon />;
    }

    if (route === ROUTES.Drivers) {
      return <AddDriversIcon />;
    }
  };

  const handleDownloadButtonClick = () => {
    switch (route) {
      case ROUTES.VehicleFiles:
        return openDialog(VEHICLE_FILES_ACTIONS.Download);
      case ROUTES.DriverFiles:
        return openDialog(DRIVER_FILES_ACTIONS.Download);
      default:
        return () => console.log("download");
    }
  };

  const handleFileSettingsButtonClick = () => {
    switch (route) {
      case ROUTES.VehicleFiles:
        return openDialog(VEHICLE_FILES_ACTIONS.FileSettings);
      case ROUTES.DriverFiles:
        return openDialog(DRIVER_FILES_ACTIONS.FileSettings);
      default:
        return () => console.log("File settings button");
    }
  };

  const handleInviteUserButtonClick = () => {
    openDialog(USERS_ACTIONS.InviteUser);
  };

  const handleMoveItemButtonClick = () => {
    openDialog(COMPANIES_ACTIONS.MoveCompany);
  };

  const showAddButtonInMenu = desktopDetailsSection && moreActions && !fromLg;
  const isInitialLoadingLogic = resetDetailsSection || initialLoading;

  return (
    <>
      <>
        {addItem && !showAddButtonInMenu && (
          <Button
            fullWidth={toSm}
            id={TUTORIAL_IDS.AddResourceButton}
            hasRectangleLoader={toSm}
            variant={BUTTON_VARIANTS.TextOnly}
            size="small"
            color="primary"
            onClick={handleAddItemClick}
            isInitialLoading={isInitialLoadingLogic}
            loaderWidth={actionButtonLoaderWidthToSm}
          >
            {generateAddItemButtonText()}
          </Button>
        )}
        {moreActions && (
          <Button
            fullWidth={toSm}
            hasRectangleLoader={toSm}
            variant="iconRight"
            size="small"
            color="white"
            isInitialLoading={isInitialLoadingLogic}
            icon={<ExpandMoreIcon />}
            onClick={handleOpenMenu}
          >
            {t("Button##more actions")}
          </Button>
        )}

        <Menu
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center"
          }}
          css={css({
            marginTop: "3px",
            "& .MuiMenu-paper": {
              minWidth: "211px"
            }
          })}
        >
          {addItem && showAddButtonInMenu && (
            <ListChoice
              title={generateAddItemButtonText()}
              iconLeft={getAddItemIcon()}
              onClick={() => {
                handleAddItemClick();
              }}
            />
          )}

          {moreActions?.includes(MORE_ACTIONS_KEYS.Import) && (
            <ListChoice
              title={generateImportItemButtonText()}
              iconLeft={<FileDownloadIcon />}
              onClick={handleImportItemClick}
            />
          )}

          {moreActions?.includes(MORE_ACTIONS_KEYS.Export) && (
            <ListChoice
              title={generateExportItemButtonText()}
              iconLeft={<FileUploadIcon />}
              onClick={handleExportItemClick}
            />
          )}
        </Menu>

        {importItem && (
          <Button
            variant={BUTTON_VARIANTS.TextOnly}
            fullWidth={toSm}
            hasRectangleLoader={toSm}
            size="small"
            color="white"
            onClick={handleImportItemClick}
            isInitialLoading={isInitialLoadingLogic}
            loaderWidth={actionButtonLoaderWidthToSm}
          >
            {t("Button##import")}
          </Button>
        )}
        {download && (
          <Button
            data-testid="header-download-button"
            fullWidth={toSm}
            hasRectangleLoader={toSm}
            variant={BUTTON_VARIANTS.TextOnly}
            size="small"
            color="primary"
            disabled={tableRowData?.length === 0 || selectedRows?.length === 0}
            onClick={handleDownloadButtonClick}
            isInitialLoading={isInitialLoadingLogic}
            loaderWidth={actionButtonLoaderWidthToSm}
          >
            {t("Button##download")}
          </Button>
        )}
        {fileSettings && (
          <Button
            data-testid="header-file-settings-button"
            fullWidth={toSm}
            hasRectangleLoader={toSm}
            variant={BUTTON_VARIANTS.TextOnly}
            size="small"
            color="white"
            disabled={tableRowData?.length === 0}
            onClick={handleFileSettingsButtonClick}
            isInitialLoading={isInitialLoadingLogic}
            loaderWidth={actionButtonLoaderWidthToSm}
          >
            {t("Button##file settings")}
          </Button>
        )}
        {inviteUser && (
          <Button
            data-testid="header-invite-user-button"
            fullWidth={toSm}
            hasRectangleLoader={toSm}
            variant={BUTTON_VARIANTS.TextOnly}
            size="small"
            color="primary"
            onClick={handleInviteUserButtonClick}
            isInitialLoading={isInitialLoadingLogic}
            loaderWidth={actionButtonLoaderWidthToSm}
          >
            {t("Button##invite user")}
          </Button>
        )}
        {moveItem && (
          <Button
            data-testid="header-move-item-button"
            fullWidth={toSm}
            hasRectangleLoader={toSm}
            variant={BUTTON_VARIANTS.TextOnly}
            size={BUTTON_SIZES.Small}
            color={BUTTON_COLORS.Secondary}
            onClick={handleMoveItemButtonClick}
            isInitialLoading={isInitialLoadingLogic}
            loaderWidth={actionButtonLoaderWidthToSm}
          >
            {t("Button##move")}
          </Button>
        )}
      </>
      {/* Dialogs below as children*/}
      {children}
    </>
  );
};

export default PageHeaderActions;
